import React, { useState, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Forms, Modal, SaveButton, SystemText, Copy } from '@salesperformanceint/e4shared';

import { setPickerFilters, toggleFormHelp } from 'actions/ui';
import { addDays, endOfDay, isPast } from 'helpers/dates';
import { ModalPicker } from 'components/user/modalPicker';
import { CourseKind } from 'enums/courseKind';

export const Schedule = ({ show, close, onSchedule, users, pathway }) => {
    if (!pathway) {
        return null;
    }

    const dispatch = useDispatch();

    const today = useMemo(() => new Date(), []);

    const defaultValues = useMemo(() => {
        return {
            ids: [],
            date_Due: addDays(today, 30),
            launch: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 9, 0),
            quickCheckScheduledDate: addDays(today, 30),
            quickCheckDuration: 12,
        };
    }, []);

    const [tab, setTab] = React.useState('assign');
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [values, setValues] = useState({});
    const [disabledUsers, setDisabledUsers] = React.useState([]);

    const [newEnrollmentCount, setNewEnrollmentCount] = useState(1);
    const [existingEnrollmentCount, setExistingEnrollmentCount] = useState(2);

    const scheduleQuickCheck =
        pathway.quickCheckPathway?.choice === CourseKind.Program &&
        !pathway.quickCheckPathway?.launchOnPathwayCompletion;

    const validate = assignment => {
        let result = true;
        const theseErrors = {};

        if (!assignment?.date_Due || assignment?.date_Due?.length === 0 || assignment?.date_Due === '1970-01-01') {
            theseErrors.date_Due = SystemText('PathwayTracker/Schedule/Fields/Date/Error');
            result = false;
        } else if (isPast(endOfDay(assignment.date_Due))) {
            theseErrors.date_Due = SystemText('PathwayTracker/Schedule/Fields/Date/Error/Past');
            result = false;
        } else if (assignment.date_Due < assignment.launch) {
            theseErrors.launch = SystemText('PathwayTracker/Schedule/Fields/Launch/Error/Past');
            result = false;
        } else if (scheduleQuickCheck && assignment.quickCheckScheduledDate < assignment.launch) {
            theseErrors.launch = SystemText('PathwayTracker/Schedule/Fields/QuickCheck/Error/Past');
            result = false;
        }

        setErrors(theseErrors);

        if (!result) {
            document.getElementsByClassName('modal')[0].scrollTop = 0;
        }

        return result;
    };

    const save = () => {
        if (validate(values)) {
            onSchedule(values, () => closeModal());
        } else {
            closeModal();
        }
        setSaving(false);
    };

    const closeModal = () => {
        setErrors({});
        close?.();
        setSaving(false);
        setTab('assign');

        setValues(prevValues => ({
            ...prevValues,
            date_Due: defaultValues.date_Due,
            launch: defaultValues.launch,
            ids: disabledUsers.map(u => u.id),
            quickCheckScheduledDate: defaultValues.quickCheckScheduledDate,
        }));

        dispatch(setPickerFilters([]));
    };

    React.useEffect(() => {
        setValues(defaultValues);
    }, [defaultValues]); // Only run once on mount

    React.useEffect(() => {
        const inProgressMessage = SystemText('PathwayTracker/Schedule/DisabledUser/InProgress');
        const temp = [...users].reduce(
            (a, c) =>
                c.date_Assigned && !c.date_Completed
                    ? a.concat([
                          {
                              id: c.id,
                              message: inProgressMessage,
                          },
                      ])
                    : a,
            []
        );

        setDisabledUsers(temp);
    }, [users]);

    React.useEffect(() => {
        setValue(
            'ids',
            disabledUsers.map(u => u.id)
        );
    }, [disabledUsers]);

    const setValue = useCallback((property, value) => {
        setValues(prevValues => ({
            ...prevValues,
            [property]: value,
        }));
    }, []);

    React.useEffect(() => {
        if (!values.ids) {
            return;
        }

        const newCount = values.ids.length;
        const existingCount = disabledUsers.length;

        setNewEnrollmentCount(newCount - existingCount);
        setExistingEnrollmentCount(existingCount);
    }, [values?.ids, disabledUsers]);

    const changeTab = () => {
        setTab(tab === 'assign' ? 'set' : 'assign');
    };

    const footer = (
        <div>
            {tab === 'assign' ? (
                <button
                    data-testid="assign-button"
                    className="btn btn-primary"
                    onClick={changeTab}
                    disabled={!values.ids?.length}
                >
                    {SystemText('General/Buttons/Next')}
                </button>
            ) : (
                <SaveButton
                    data-testid="save-button"
                    saving={saving}
                    setSaving={setSaving}
                    className="btn btn-success"
                    disabled={!values.ids?.length}
                    onClick={save}
                >
                    {SystemText('General/Buttons/Schedule')}
                </SaveButton>
            )}
            <button data-testid="cancel-button" className="btn" onClick={() => closeModal(true)}>
                {SystemText('General/Buttons/Cancel')}
            </button>
        </div>
    );

    const scheduleForm = (
        <div className="form-group mt10" id="manage-task-form">
            <Forms.Date
                data-testid="field-launch-date"
                showTime
                mandatory
                value={values.launch || defaultValues.launch}
                help={SystemText('PathwayTracker/Schedule/Fields/Launch/Help')}
                onChange={value => setValue('launch', value)}
                label={SystemText('PathwayTracker/Schedule/Fields/Launch/Label')}
                error={errors.launch}
            />
            <Forms.Date
                data-testid="field-due-date"
                value={values.date_Due || defaultValues.date_Due}
                help={SystemText('PathwayTracker/Schedule/Fields/Date/Help')}
                onChange={value => setValue('date_Due', value)}
                label={SystemText('PathwayTracker/Schedule/Fields/Date/Label')}
                mandatory
                error={errors.date}
            />

            {scheduleQuickCheck && (
                <>
                    <hr />
                    <Forms.Date
                        data-testid="field-schedule-quickcheck-date"
                        value={values.quickCheckScheduledDate || defaultValues.quickCheckScheduledDate}
                        help={SystemText('PathwayTracker/Schedule/Fields/ReinforcmentDate/Help')}
                        onChange={value => setValue('quickCheckScheduledDate', value)}
                        label={SystemText('PathwayTracker/Schedule/Fields/ReinforcmentDate/Label')}
                        mandatory
                        error={errors.date}
                    />
                    {/* <Forms.Selectv2
                                label="Adjust the Reinforcement duration"
                                value={values.quickCheckDuration || defaultValues.quickCheckDuration}
                                onChange={value => setValue('quickCheckDuration', value)}
                                help=""
                                mandatory
                                data={[
                                    { name: '6 Weeks', id: 6 },
                                    { name: '9 Weeks', id: 9 },
                                    { name: '12 Weeks', id: 12 },
                                    { name: '15 Weeks', id: 15 },
                                    { name: '18 Weeks', id: 18 },
                                ]}
                            /> */}
                </>
            )}
        </div>
    );

    return (
        <Modal
            data-testid="pathway-schedule-modal"
            show={show}
            close={closeModal}
            footer={footer}
            toggleFormHelp={() => dispatch(toggleFormHelp())}
            tab={tab}
            setTab={setTab}
            className="modal-body-full modal-920"
            tabs={['assign', 'set']}
            tabIcons={
                tab === 'assign'
                    ? ['fa-circle-1 fa-solid fa-tab', 'fa-circle-2 fa-tab']
                    : ['fa-circle-check fa-tab', 'fa-circle-2 fa-solid fa-tab']
            }
            iconsPre
            tabNames={{
                assign: SystemText('PathwayTracker/Schedule/Tabs/Assign'),
                set: SystemText('PathwayTracker/Schedule/Tabs/Set'),
            }}
        >
            {tab === 'assign' ? (
                <ModalPicker value={values?.ids} disabledUsers={disabledUsers} onChange={ids => setValue('ids', ids)} />
            ) : (
                <div>
                    {values?.ids?.length ? (
                        <div className="flex" style={{ minHeight: '450px' }}>
                            <div className="flex-80">{scheduleForm}</div>
                            <div className="flex-20 border-left-light p10">
                                <div>
                                    <b>{SystemText('PathwayTracker/Schedule/EnrolledUsers')}</b>
                                    <span className="ml5 label label-success">{values?.ids?.length}</span>
                                </div>
                                <div className="mt10">
                                    <Copy
                                        value={SystemText(
                                            'PathwayTracker/Schedule/EnrolledUsersMessage',
                                            newEnrollmentCount,
                                            pathway.name,
                                            existingEnrollmentCount
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="mb20 mt20 flex flex-center">
                            <i className="fa fa-warning fa-lg mr10 text-danger" />
                            <span className="flex-grow">
                                <Copy value={SystemText('PathwayTracker/Schedule/NoUsers')} />
                            </span>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
};

export default Schedule;
